import React, { useContext, useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'lib/hooks/redux';
import { getIconType, getPaymentName } from 'lib/utils/payments';
import { setPaymentDummy } from 'store/paymentMethod/actions';
import { useTranslation } from 'react-i18next';
import { ErrorContext } from 'containers/aio/PaymentMethodsContainer/context';
import { setPaymentWithoutCvv } from 'store/paymentMethod/ripleyCard.slice';
import { setPaymentMethod } from 'store/paymentMethod/cybersource.slice';
import { RootState } from 'types/store';

import PaymentMethodItem from 'components/payment/PaymentMethodItem';
import Title from 'components/payment/CreditCardTitle';
import { setContextError } from 'lib/utils/paymentsErrors';
import { getEnvironment } from 'config';
import { deleteCard } from 'store/card/actions';
import { setPreferredCard } from 'store/cardToken/cardToken.slice';
import { updateLoadingPreferred } from 'store/paymentMethod/paymentMethod.slice';
import { country } from '../../../consts';

export type TokenItemProps = {
    token: CardToken;
    disabled?: boolean;
};

export const TokenItem: React.FC<TokenItemProps> = ({ token, disabled }: TokenItemProps) => {
    const { t } = useTranslation('error');
    const dispatch = useAppDispatch();
    const ctx = useContext(ErrorContext);
    const cards = useAppSelector((state: RootState) => state.card);
    const isOpen = useAppSelector((state: RootState) =>
        state.cardToken.data.selected.includes(token.id),
    );
    const isSelected = token.id === cards.selected;
    const pm = useAppSelector((state: RootState) => state.paymentMethod.data);
    const couponLoading = useAppSelector(
        (state: RootState) => state.coupon.status === 'pending' || state.cart.status !== 'ok',
    );
    const isLoading = cards.byId[token.id]?.status === 'pending';

    const {
        app: { enableNewPaymentMethods },
    } = getEnvironment();

    useEffect(() => {
        (async () => {
            if (
                token.preferred &&
                !isLoading &&
                enableNewPaymentMethods &&
                !pm.inProgressCyb &&
                country === 'CL'
            ) {
                dispatch(updateLoadingPreferred(true));
                const payload = { paymentMethod: token.paymentMethod, cardToken: token };
                if (token.paymentMethod === 'Cybersource') {
                    await dispatch(setPaymentMethod({ cardToken: token })).unwrap();
                } else {
                    await dispatch(setPaymentDummy(payload)).unwrap();
                }
                dispatch(updateLoadingPreferred(false));
            }
        })();
        // eslint-disable-next-line
    }, []);

    if (isSelected || isOpen) {
        return null;
    }

    const isBlocked =
        (pm.status === 'pending' && !isLoading) || couponLoading || disabled || pm.loadingPreferred;

    const handleOnClick = async () => {
        if (!isLoading) {
            const payload = { paymentMethod: token.paymentMethod, cardToken: token };
            try {
                if (token.paymentMethod == 'RipleyCard' && token.settings?.rules.cvvFreeAvailable) {
                    await dispatch(setPaymentWithoutCvv(token)).unwrap();
                } else if (token.paymentMethod == 'Cybersource') {
                    await dispatch(setPaymentMethod({ cardToken: token })).unwrap();
                } else {
                    await dispatch(setPaymentDummy(payload)).unwrap();
                }

                if (country === 'CL') {
                    await handleOnSetPreferredCard();
                }
            } catch (e) {
                setContextError(ctx, e, t);
            }
        }
    };

    const handleOnDelete = async (
        e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
        e.stopPropagation();
        dispatch(deleteCard({ cardId: token.id, hard: true }));
    };

    const handleOnSetPreferredCard = async () => {
        if (enableNewPaymentMethods) {
            await dispatch(setPreferredCard(token.id)).unwrap();
        }
    };

    const paymentName = getPaymentName(token.paymentMethod, token.type, token.maskedPan);
    const iconType = getIconType(token.paymentMethod, token.type, token.maskedPan);

    return (
        <PaymentMethodItem
            paymentMethodName={paymentName}
            type={iconType}
            widthIcon="63px"
            additionalInfo={<Title card={token} />}
            onClick={handleOnClick}
            selected={isSelected}
            isLoading={isLoading}
            blocked={isBlocked}
            enableDelete={enableNewPaymentMethods}
            handleOnDelete={handleOnDelete}
        />
    );
};
